<script lang="ts" setup>
	import type { TestimonialContext } from '~/stores'
	import type { IRequestResponse } from '~/types'
	import { useApiCache } from '../../composables/useApiCache'

	const props = defineProps<{
		testimonies?: TestimonialContext
	}>()

	const { data, status } = useLazyFetch<IRequestResponse<TestimonialContext>>('/api/bff/v1/testimonials', {
		immediate: !props.testimonies?.length,
		key: 'testimonials',
		...useApiCache(1000),
		server: false
	})

	const testimonyList = computed(() => (props.testimonies?.length ? props.testimonies : (data.value?.data ?? [])))
</script>

<template>
	<div class="flexed flex-col">
		<ui-title-header title="It Works!" class="text-center" />

		<div
			class="flex sm:gap-x-4 overflow-hidden invisible-scrollbar pause-animation pt-16 items-stretch sm:pt-20 pb-10 w-full"
		>
			<div
				class="flex items-center gap-x-4 duration-40s flex-none animate-vertical-scroll"
				:style="{ '--animation-move-count': '-230%' }"
			>
				<client-only>
					<template v-if="status === 'pending'">
						<u-skeleton
							v-for="index in 3"
							:key="`loader-${index}`"
							class="min-h-[248px] w-[335px] sm:w-[400px]"
						/>
					</template>
					<template v-else>
						<div
							v-for="(testimony, index) in testimonyList"
							:key="`tesitmonies-${index + 1}`"
							class="testimony-card-style group card-border-orange self-stretch custom-transition-2 rounded-xl"
						>
							<div class="relative z-10">
								<div class="pb-5 flex items-center border-b gap-x-3">
									<div
										:class="[
											'h-[50px] justify-center text-blue-950 dark:text-gray-500 w-[50px] flex-none rounded-full font-semibold flexed text-sm',
											testimony.color_tag ?? 'bg-gray-50'
										]"
									>
										{{ String(testimony.user_name ?? '').split(' ')[0]?.[0] ?? '' }}
										{{ String(testimony.user_name ?? '').split(' ')[1]?.[0] ?? '' }}
									</div>

									<div class="space-y-1">
										<strong
											class="font-semibold text-blue-950 dark:text-gray-300 sm:text-xl text-sm xs:text-[1.125em]"
										>
											{{ testimony.user_name }}
										</strong>
										<p class="text-gray-500 dark:text-gray-400 text-xs">
											{{ testimony.planCategory ?? '---' }}
										</p>
									</div>
								</div>

								<p class="text-gray-700 text-sm dark:text-gray-400 xs:leading-[20px] pt-5">
									{{ testimony.testimony ?? '' }}
								</p>
							</div>

							<div class="absolute right-0 bottom-0">
								<tools-icon
									dir="random"
									icon="quote"
									height="93"
									width="100"
									path-class="group-hover:fill-main"
								/>
							</div>
						</div>
					</template>
				</client-only>
			</div>
		</div>
	</div>
</template>
